import React, { useEffect, useState } from 'react';
import './BrandMarkSpinner.css';

const NAV_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/carmen-logo.png`;
const GOLD = { r: 226, g: 179, b: 42 };
const SHOW_DELAY_MS = 300;

export type BrandMarkSize = 'small' | 'medium' | 'large';
export type BrandMarkTone = 'light' | 'dark';

interface BrandMarkSpinnerProps {
  size?: BrandMarkSize;
  tone?: BrandMarkTone;
  label?: string;
  caption?: boolean;
}

let mattedMark: Promise<string> | null = null;

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * The navigation file is gold on a white plate. Dark stages need that plate
 * removed at display time. The file itself is not rewritten.
 */
const transparentMark = (src: string): Promise<string> => {
  if (!mattedMark) {
    mattedMark = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          resolve(src);
          return;
        }
        context.drawImage(image, 0, 0);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = frame.data;
        const scale = 1 / (1 - GOLD.b / 255);
        for (let index = 0; index < pixels.length; index += 4) {
          const coverage = Math.min(1, Math.max(0, (1 - pixels[index + 2] / 255) * scale));
          pixels[index] = GOLD.r;
          pixels[index + 1] = GOLD.g;
          pixels[index + 2] = GOLD.b;
          pixels[index + 3] = Math.round(coverage * 255);
        }
        context.putImageData(frame, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => resolve(src);
      image.src = src;
    });
  }
  return mattedMark;
};

const BrandMarkSpinner: React.FC<BrandMarkSpinnerProps> = ({
  size = 'medium',
  tone = 'light',
  label = 'Loading',
  caption = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [markSrc, setMarkSrc] = useState(tone === 'light' ? NAV_LOGO_SRC : '');

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (tone === 'light') {
      setMarkSrc(NAV_LOGO_SRC);
      return undefined;
    }
    let cancelled = false;
    transparentMark(NAV_LOGO_SRC).then((src) => {
      if (!cancelled) setMarkSrc(src);
    });
    return () => {
      cancelled = true;
    };
  }, [tone]);

  const shown = visible && Boolean(markSrc);

  return (
    <div
      className={`brand-mark brand-mark--${size}${shown ? ' is-shown' : ''}${reducedMotion ? ' is-reduced' : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="brand-mark__sr">{label}</span>
      <span className="brand-mark__rotor" aria-hidden="true">
        {markSrc && <img src={markSrc} alt="" draggable={false} />}
      </span>
      {caption && <span className="brand-mark__caption" aria-hidden="true">{label}</span>}
    </div>
  );
};

export default BrandMarkSpinner;
